import React, { useState, Fragment, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useBlocker } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Button } from 'components/buttons';
import { Dialog } from '@blueprintjs/core';
import './Notifications.css';

export function AlertUnsavedData(
    {
        isDirtySamePage = false,
        onCancelLeave = null,
        onConfirmLeave = null,
        scrollToBottomOnCancel = false,
        preserveSearch = false,
        isDirty
    }
) {

    var [alertOpen, setAlertOpen] = useState(false);
    var [nextLocation, setNextLocation] = useState(null);
    var navigate = useNavigate();
    var dispatch = useDispatch();

    var blockNavigationRef = useRef();
    var attemptingRedirect = useSelector(state => state.dirtyData.attemptingRedirect);
    var redirectCallback = useSelector(state => state.dirtyData.task);

    var blocker = useBlocker(function() {
        return blockNavigationRef.current === true;
    });

    useEffect(function() {
        if (blocker.state === 'blocked') {
            setAlertOpen(true);
            setNextLocation(blocker.location);
        }
    }, [blocker.state]);

    useEffect(function () {
        if (isDirtySamePage) {
            setAlertOpen(true);
        } else {
            setAlertOpen(false);
        }
    }, [isDirtySamePage]);

    useEffect(function () {
        if (attemptingRedirect === true) {
            setAlertOpen(true);
        }
    }, [attemptingRedirect]);

    useEffect(onDirtyChange, [isDirty]);

    // A page restored from the browser's back/forward cache keeps the Redux store from the moment
    // it was frozen, so a redirect armed for that abandoned navigation is still pending and would
    // reopen this dialog on every page that renders it. Cancel the redirect; the dirty flag is left
    // alone because the restored page also keeps the unsaved values it describes.
    useEffect(function () {
        function onPageShow(event) {
            if (event.persisted === true) {
                dispatch({ type: 'CANCEL_REDIRECT' });
            }
        }

        window.addEventListener('pageshow', onPageShow);

        return function () {
            window.removeEventListener('pageshow', onPageShow);
        };
    }, []);

    function onDirtyChange() {
        blockNavigationRef.current = isDirty;
    }

    function handleStayNavigationClick() {
        setAlertOpen(false);
        if (blocker.state === 'blocked') {
            blocker.reset();
        }
        if (scrollToBottomOnCancel) {
            window.scrollTo({ left: 0, top: document.body.scrollHeight, behavior: "smooth" });
        }
        if (attemptingRedirect === true) {
            dispatch({ type: 'CANCEL_REDIRECT' });
        }
        if (onCancelLeave) {
            onCancelLeave();
        }
    }

    function handleLeaveNavigationClick() {
        setAlertOpen(false);

        var nextLocationWithSearch = nextLocation?.pathname + (preserveSearch ? (nextLocation?.search ?? "") : "");

        if (onConfirmLeave) {
            if (blocker.state === 'blocked') {
                blocker.reset();
            }
            // This path never runs an armed redirect, so cancel it — otherwise it stays armed
            // and reopens this dialog on every page that renders it.
            if (attemptingRedirect === true) {
                dispatch({ type: 'CANCEL_REDIRECT' });
            }
            onConfirmLeave(nextLocationWithSearch);
            return;
        }

        if (attemptingRedirect === true) {
            if (blocker.state === 'blocked') {
                blocker.reset();
            }
            if (typeof redirectCallback == 'function') {
                // Consume the redirect before running it, so an abandoned full-page navigation
                // cannot leave it armed and reopen this dialog on other pages.
                dispatch({ type: 'CANCEL_REDIRECT' });
                redirectCallback();
            } else {
                dispatch({ type: 'ALLOW_REDIRECT' });
            }
        } else {
            dispatch({ type: 'RESET_DIRTY_FLAG' });
            blockNavigationRef.current = false;
            // A dialog opened by a redirect that has since been consumed elsewhere has no destination
            // of its own, so leaving only closes it.
            if (blocker.state === 'blocked') {
                blocker.proceed();
            } else if (nextLocation != null) {
                navigate(nextLocationWithSearch);
            }
        }
    }

    return (
        <Fragment>
            <Dialog
                isOpen={alertOpen}
                title="Unsaved Changes"
                isCloseButtonShown={false}
                className="modal-holder small"
            >
                <div className="bp6-dialog-body">
                    <p>If you leave now, the changes will not be saved. Do you want to discard the changes and continue?</p>
                </div>

                <div className="bp6-dialog-footer">
                    <div className="bp6-dialog-footer-actions">
                        <Button text="Leave page" intent="danger" onClick={handleLeaveNavigationClick}></Button>
                        <Button text="Stay on page" intent="primary" onClick={handleStayNavigationClick}></Button>
                    </div>
                </div>
            </Dialog>
        </Fragment>
    );
}

AlertUnsavedData.propTypes = {
    isDirty: PropTypes.bool.isRequired,
    isDirtySamePage: PropTypes.bool,
    onCancelLeave: PropTypes.func,
    onConfirmLeave: PropTypes.func,
    scrollToBottomOnCancel: PropTypes.bool,
    preserveSearch: PropTypes.bool
};
